import React from "react"

import { PauseAnalysisConfig } from "@/metrics/registry/configuration"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import { ThresholdMetricBox } from "@/components/ThresholdMetricBox"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
  SubvalueByTimestamp,
} from "@/types/RunMetricOutput"
import { stringifyUnknownValue } from "@/utils/stringifyUnknownValue"

import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { parseResultJson } from "../helpers/parseResultJson"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"
import { readPauseAnalysisValues } from "./pauseAnalysisValues"

const CATEGORY = "pause_analysis"

interface PauseAnalysisResultJson {
  subvalues_by_timestamp?: SubvalueByTimestamp[]
}

export class PauseAnalysisMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const
  unit = "pauses/min"
  aggregate_units = "pauses/min"

  get hasVersionedConfig() {
    return true
  }

  isMetricOutputCurrentVersion(metricOutput: {
    result_json?: Record<string, unknown> | null
  }): boolean {
    const executionThreshold =
      metricOutput.result_json?.min_pause_duration_seconds
    if (executionThreshold == null) {
      return true
    }
    const currentThreshold = this.metric_metadata?.min_pause_duration_seconds
    if (currentThreshold == null) {
      return true
    }
    return currentThreshold === executionThreshold
  }

  getVersionDiff(metricOutput: {
    result_json?: Record<string, unknown> | null
  }): Array<{ field: string; current: string; previous: string }> {
    const diffs: Array<{ field: string; current: string; previous: string }> =
      []
    const executionThreshold =
      metricOutput.result_json?.min_pause_duration_seconds
    const currentThreshold = this.metric_metadata?.min_pause_duration_seconds
    if (
      executionThreshold != null &&
      currentThreshold != null &&
      currentThreshold !== executionThreshold
    ) {
      diffs.push({
        field: "Min Pause Duration (s)",
        current: String(currentThreshold),
        previous: stringifyUnknownValue(executionThreshold),
      })
    }
    return diffs
  }

  _renderDeepDive(mo: RunMetricOutput): React.ReactNode {
    const values = readPauseAnalysisValues(mo)

    return (
      <div className="space-y-2">
        <div className="grid grid-cols-2 gap-4">
          <ThresholdMetricBox
            title="Pause Freq."
            value={values.pauseFrequency}
            unit="pauses/min"
            ranges={{ danger: 1, warning: 0.5 }}
          />
          <ThresholdMetricBox
            title="Average Pause time"
            value={values.averagePauseDuration}
            unit="s"
            ranges={{ danger: 4, warning: 2 }}
          />
          <ThresholdMetricBox
            title="Total Pause time"
            value={values.totalPauseDuration}
            unit="s"
          />
          <ThresholdMetricBox
            title="Pause threshold"
            value={values.minPauseDuration}
            unit="s"
          />
        </div>
        {(values.madAnomalousCount != null ||
          values.madZScoreMax != null ||
          values.madZScoreThreshold != null) && (
          <div className="grid grid-cols-2 gap-4">
            <ThresholdMetricBox
              title="Anomalous Pauses"
              value={values.madAnomalousCount ?? 0}
              unit="pauses"
            />
            <ThresholdMetricBox
              title="Max MAD z-score"
              value={values.madZScoreMax ?? 0}
              unit=""
            />
            <ThresholdMetricBox
              title="MAD z-score Threshold"
              value={values.madZScoreThreshold ?? 0}
              unit=""
            />
          </div>
        )}
      </div>
    )
  }

  getAudioRegions(
    mo: MetricOutputDisplaySource | undefined,
  ): AudioRegionParams[] {
    if (!mo) {
      return []
    }
    if (mo?.error_message && mo?.error_message !== "") {
      return []
    }
    try {
      // Prefer top-level subvalues_by_timestamp; cascade to typed/legacy result.
      const parsed = parseResultJson<PauseAnalysisResultJson>(mo)
      const subvalues: SubvalueByTimestamp[] =
        (mo.subvalues_by_timestamp?.length ?? 0) > 0
          ? (mo.subvalues_by_timestamp ?? [])
          : Array.isArray(parsed?.subvalues_by_timestamp)
            ? (parsed?.subvalues_by_timestamp ?? [])
            : []
      return subvalues.map((item, idx) => ({
        id: `pause:${idx}`,
        start: item.start_offset,
        end: Math.max(item.end_offset, item.start_offset + 0.5),
        color: "rgba(255, 0, 0, 0.3)",
        drag: false,
        resize: false,
      }))
    } catch {
      return []
    }
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "per_minute",
      useRegions: true,
      buildReviewedResultJson: ({ score, durationSeconds, regions }) => {
        const totalPauseDuration = regions.reduce(
          (sum, r) => sum + ((r.end ?? r.start + 0.05) - r.start),
          0,
        )
        return {
          pause_frequency: Number(score.toFixed(2)),
          total_pause_duration: Number(totalPauseDuration.toFixed(2)),
          duration_seconds:
            durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
        }
      },
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <PauseAnalysisConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(metricRecordData, onChange, textAreaRef)}
      </div>
    )
  }

  renderMetadataField(metricRecordData: MetricRecordData): React.ReactNode {
    return (
      <div className="flex flex-col gap-2">
        <div>
          <h3 className="font-semibold">Pause time threshold (seconds): </h3>
          <p>{metricRecordData.metric_metadata.min_pause_duration_seconds}</p>
        </div>
      </div>
    )
  }

  renderMetricType(): React.ReactNode {
    return "Pause Analysis"
  }
}
